import { LitElement, html } from 'lit';
import '../job-board-table/JobBoardTable';
import '../job-board-filter/JobBoardFilter';
import '../job-board-search/JobBoardSearch';
import '../job-board-listing/JobBoardListing';
import JobBoardApi from '../../../shared/api/JobBoardApi';
import AppContextConsumer from '../../../shared/context/app-context/AppContextConsumer';
import styles from './styles';

class JobBoardContainer extends LitElement {
  static styles = styles;

  static properties = {
    jobs: {
      type: Array,
    },
    filteredJobs: {
      type: Array,
      state: true,
    },
    filterOptions: {
      type: Object,
      state: true,
    },
    filterValue: {
      type: Object,
      state: true,
    },
    search: {
      type: String,
      state: true,
    },
    filterFields: {
      type: Array,
    },
    filterHidden: {
      type: Boolean,
      state: true,
    },
    numOfFiltersApplied: {
      type: Number,
      state: true,
    },
  };

  constructor() {
    super();
    this.jobs = [];
    this.filteredJobs = [];
    this.filterOptions = {}; // ie. { category: { Sales: 1 }} shows how many available based on filter
    this.filterValue = {}; // ie. { category: [ 'Sales' ]} what to filter by
    this.search = '';
    this.filterFields = [];
    this.filterHidden = false;
    this.numOfFiltersApplied = 0;
  }

  context = AppContextConsumer(this);

  #initializeEmptyOptions() {
    const options = {};

    this.filterFields.forEach((filter) => {
      options[filter] = {};
    });

    return options;
  }

  #addOptionToFilter(filterOption, job) {
    this.filterFields.forEach((filter) => {
      const optionsToAdd = job[filter].type === 'multi_select' ? job[filter].value : [job[filter].value];

      optionsToAdd.forEach((option) => {
        if (filterOption[filter][option]) {
          filterOption[filter][option] += 1;
          return;
        }

        filterOption[filter][option] = 1;
      });
    });
  }

  #getUrlParams(param, type = 'array') {
    const searchParams = new URLSearchParams(window.location.search);
    const params = searchParams.getAll(param);

    if (type === 'array') {
      const flattenParam = [];

      params.forEach((value) => flattenParam.push(...value.split(',')));
      return [...new Set(flattenParam)];
    }

    return params.join(',');
  }

  #jobMatches(job) {
    let matchesFilter = true;

    Object.keys(this.filterValue).forEach((filter) => {
      if (this.filterValue[filter].length === 0) {
        return;
      }

      if (job[filter].type === 'multi_select') {
        matchesFilter = matchesFilter && job[filter].value.some((check) => this.filterValue[filter].includes(check));
        return;
      }

      matchesFilter = matchesFilter && this.filterValue[filter].includes(job[filter].value);
    });

    const searchValue = this.search.trim();
    const matchesSearch = searchValue === ''
      || !!job.title.match(new RegExp(searchValue, 'i'))
      || !!job.content.match(new RegExp(searchValue, 'i'))
      || !!job.category.value.match(new RegExp(searchValue, 'i'))
      || !!job.location.value.join(', ').match(new RegExp(searchValue, 'i'));

    return matchesFilter && matchesSearch;
  }

  async #searchHandler(event) {
    this.search = event?.detail?.value;
    this.filteredJobs = this.jobs.filter((job) => this.#jobMatches(job));

    const filterOptions = this.#initializeEmptyOptions();

    this.filteredJobs.forEach((job) => {
      this.#addOptionToFilter(filterOptions, job);
    });

    this.filterOptions = filterOptions;
  }

  async #filterHandler(event) {
    const updatedOptions = this.#initializeEmptyOptions();
    const tempCopyOptions = this.filterOptions;

    this.filteredJobs = this.jobs.filter((job) => {
      const keepJob = this.#jobMatches(job);

      if (keepJob) {
        this.#addOptionToFilter(updatedOptions, job);
      }

      return keepJob;
    });

    Object.keys(this.filterOptions).forEach((option) => {
      if (event?.detail?.filter?.name !== option) {
        tempCopyOptions[option] = updatedOptions[option] || {};
      }

      if (event?.detail?.filter?.name === option && this.filterValue[option].length === 0) {
        tempCopyOptions[option] = updatedOptions[option] || {};
      }
    });

    this.filterOptions = { ...tempCopyOptions };
  }

  async firstUpdated() {
    try {
      const { data: { jobs, filterFields } } = await this.JobBoardApi.all();

      this.jobs = jobs;
      this.filterFields = filterFields;
      this.filteredJobs = [];
      this.filterFields.forEach((filter) => {
        this.filterValue[filter] = this.#getUrlParams(filter, 'array');
      });

      const filterOptions = this.#initializeEmptyOptions();

      this.jobs.forEach((job) => {
        if (this.#jobMatches(job)) {
          this.#addOptionToFilter(filterOptions, job);
          this.filteredJobs.push(job);
        }
      });

      this.filterOptions = filterOptions;

      const filtersApplied = Object.keys(this.filterValue).reduce(
        (accumulator, option) => accumulator + this.filterValue[option].length,
        0,
      );

      this.numOfFiltersApplied = filtersApplied;
      this.filterHidden = filtersApplied > 0;
    } catch (error) {
      console.error('Job board request error', error);
    }
  }

  connectedCallback() {
    super.connectedCallback();
    this.JobBoardApi = new JobBoardApi({ env: this.context.value?.env });

    this.search = this.#getUrlParams('search', 'string');
  }

  render() {
    return html`
      <div class="job-board-container">
        <div class="justify-content-between job-board-filter-search desktop-version">
          <tenable-job-board-filter
            class="job-filter"
            .filterOptions=${this.filterOptions}
            .filterValue=${this.filterValue}
            @filter-event=${(e) => this.#filterHandler(e)}
          >
          </tenable-job-board-filter>
          <tenable-job-board-search class="w-25" .search=${this.search} @search-event=${(e) => this.#searchHandler(e)}></tenable-job-board-search>
        </div>
        <div class="job-board-filter-search mobile-version">
          <tenable-job-board-search .search=${this.search} @search-event=${(e) => this.#searchHandler(e)}></tenable-job-board-search>
          <tenable-job-board-filter
            class="job-filter"
            .filterOptions=${this.filterOptions}
            .filterValue=${this.filterValue}
            @filter-event=${(e) => this.#filterHandler(e)}
            .numOfFiltersApplied=${this.numOfFiltersApplied}
            .filterHidden=${this.filterHidden}
            ?mobile=${true}
          >
          </tenable-job-board-filter>
        </div>
        <div class="job-listing-container">
          <tenable-job-board-table class="desktop-version" .jobs=${this.filteredJobs}></tenable-job-board-table>
          <tenable-job-board-listing class="mobile-version" .jobs=${this.filteredJobs}></tenable-job-board-listing>
        </div>
      <div>
    `;
  }
}

customElements.define('tenable-job-board-container', JobBoardContainer);
